import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BundleReport, BundleSubmitResult } from '../api/bundle';
import { ApiError, MsgResult } from '../api/request';
import { freshReport } from './bundleIssues';

// 材料/设备创建向导共用的预检状态 (2026-09-29).
//
// 预检原先只挂在「下一步」上: 从「跳过」或左侧步骤栏进到提交页时不会重跑, 提交页展示的
// 还是改动前那份报告 (如"勾了 22 项工艺但没参数"的告警没出来), 提交按钮也按它放行 ——
// 服务端重跑预检后以"需确认告警"拒掉, 而界面仍显示"告警 0", 用户无从确认, 永远提交不了。
//
// 这里统一三条:
// 1. stale: 当前载荷与上次预检的载荷 (按实际发出的请求体比) 不一致, 报告即过期, 不能拿来放行;
// 2. autoCheck 为真 (提交页) 且报告过期时自动重跑一次预检;
// 3. adoptReport: 提交被拒时把服务端那次的报告收下, 界面与服务端判定对齐。

type Submit<P> = (payload: P, opts: { dryRun?: boolean }) => Promise<MsgResult<BundleSubmitResult>>;

export interface BundleCheck {
    /** 展示用报告 (已撤掉改过的那几条) */
    report: BundleReport | null;
    /** 服务端最后一次原始判定 */
    rawReport: BundleReport | null;
    /** 载荷在上次预检之后改过 (或从没预检过): 报告不可作提交依据 */
    stale: boolean;
    checking: boolean;
    error: string;
    runCheck: () => Promise<boolean>;
    /** 收下提交接口返回的报告 (提交被拒时服务端重跑过预检) */
    adoptReport: (rep: BundleReport) => void;
}

export function useBundleCheck<P>(
    payload: P,
    toRequest: (p: P) => P,
    submit: Submit<P>,
    autoCheck: boolean,
): BundleCheck {
    const [report, setReport] = useState<BundleReport | null>(null);
    // 预检时的载荷快照: 之后哪条 issue 指向的值被改了, 那条就先不显示 (见 utils/bundleIssues)
    const [checkedPayload, setCheckedPayload] = useState<P | null>(null);
    const [checking, setChecking] = useState(false);
    const [error, setError] = useState('');
    // payloadRef 让 runCheck 始终读到最新载荷 (避免闭包捕获旧值)
    const payloadRef = useRef(payload);
    payloadRef.current = payload;
    const inFlight = useRef(false);

    const runCheck = useCallback(async (): Promise<boolean> => {
        inFlight.current = true;
        setChecking(true);
        setError('');
        try {
            const sent = payloadRef.current;
            const { data } = await submit(toRequest(sent), { dryRun: true });
            setReport(data.report);
            setCheckedPayload(sent);
            return data.report.ok;
        } catch (e) {
            setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
            return false;
        } finally {
            inFlight.current = false;
            setChecking(false);
        }
    }, [submit, toRequest]);

    const adoptReport = useCallback((rep: BundleReport) => {
        setReport(rep);
        setCheckedPayload(payloadRef.current);
    }, []);

    // 按请求体比: 只动了不发送的草稿字段 (如空串) 不算改动, 也避免同内容新对象引起反复重跑
    const currentReq = useMemo(() => JSON.stringify(toRequest(payload)), [payload, toRequest]);
    const checkedReq = useMemo(
        () => (checkedPayload ? JSON.stringify(toRequest(checkedPayload)) : ''),
        [checkedPayload, toRequest],
    );
    const stale = !report || currentReq !== checkedReq;

    useEffect(() => {
        if (autoCheck && stale && !inFlight.current) void runCheck();
        // 失败后不自动重试 (会刷接口); 载荷再变或重新进入提交页时再跑
    }, [autoCheck, stale, currentReq, runCheck]);

    const shownReport = useMemo(
        () => freshReport(report, payload, checkedPayload),
        [report, payload, checkedPayload],
    );

    return { report: shownReport, rawReport: report, stale, checking, error, runCheck, adoptReport };
}
